// Based off the useMeasure hook from useHooks.com/usemeasure(ui.dev). Converted to typescript and added a debounce. https://github.com/uidotdev/usehooks/blob/main/index.js

import React from 'react'

type Dimensions = {
  width: number
  height: number
}

// Memoized debounce function
const useDebounce = <T extends (...args: any[]) => void>(func: T, delay: number) => {
  return React.useMemo(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    return (...args: Parameters<T>) => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      timeoutId = setTimeout(() => {
        func(...args)
      }, delay)
    }
  }, [func, delay])
}

export function useDimensions(
  debounceDelay: number = 100,
): [React.RefCallback<HTMLElement>, Dimensions] {
  const [dimensions, setDimensions] = React.useState<Dimensions>({
    width: 0,
    height: 0,
  })

  const observerRef = React.useRef<ResizeObserver | null>(null)
  const previousDimensions = React.useRef<Dimensions>(dimensions)

  const debouncedSetDimensions = useDebounce((width: number, height: number) => {
    if (
      width !== previousDimensions.current.width ||
      height !== previousDimensions.current.height
    ) {
      setDimensions({ width, height })
      previousDimensions.current = { width, height }
    }
  }, debounceDelay)

  const customRef = React.useCallback(
    (node: HTMLElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }

      if (node?.nodeType === Node.ELEMENT_NODE) {
        const observer = new ResizeObserver(([entry]) => {
          if (entry && entry.borderBoxSize) {
            const { inlineSize: width, blockSize: height } = entry.borderBoxSize[0]
            debouncedSetDimensions(width, height)
          }
        })

        observer.observe(node)
        observerRef.current = observer
      }
    },
    [debouncedSetDimensions],
  )

  // Add cleanup effect
  React.useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [])

  return [customRef, dimensions]
}
